import { ComponentSync } from '../ComponentSync.js';
import { BadImplementationException, compare, deepClone } from '@nu-art/ts-common';
import { ModuleFE_BrowserHistoryV2 } from '../../modules/ModuleFE_BrowserHistoryV2.js';
/**
 * @abstract
 * @class
 * Expansion on ComponentSync, this component takes a [ProtoComponentDef]{@link ProtoComponentDef} type that
 * defines its interaction with the URL hash. </br>
 * Constrained by the [ProtoComponentDef]{@link ProtoComponentDef}, the component will take an array of query param keys (string[]), and generate, provide functionality
 * and listen on changes in the URL hash. </br>
 * @augments ComponentSync
 * @implements OnUrlParamsChangedListenerV2
 * @copyright nu-art
 */
export class ProtoComponent extends ComponentSync {
    // ######################## Life Cycle ########################
    /**
     * Listener for URL changes triggered by ModuleFE_BrowserHistoryV2.
     * Will trigger deriveStateFromProps with the new state of the query params set to be the previous state,
     * if there were changes in the hash that are relevant to this component.
     */
    __onUrlParamsChangedV2 = () => {
        const previousResultsObject = this.state?.previousResultsObject;
        const queryParams = this.getQueryParamResultsObject();
        const hasChanges = !compare(queryParams, previousResultsObject);
        this.logDebug(`Query params has changes: ${hasChanges}`, previousResultsObject, queryParams);
        if (hasChanges) {
            this.reDeriveState({ previousResultsObject: deepClone(queryParams) });
        }
    };
    // ######################## Class Methods ########################
    /**
     * Generates an object connection each query param key given in the props to its current value as read from the URL
     */
    getQueryParamResultsObject() {
        try {
            return this.props.keys.reduce((map, key) => {
                map[key] = this.getQueryParam(key);
                return map;
            }, {});
        }
        catch (err) {
            throw new BadImplementationException('proto component implementation must implement default props with keys', err);
        }
    }
    // ######################## Singulars
    /**
     * Will set a value in the URL hash for a given key.
     * @param key
     * @param value
     */
    setQueryParam(key, value) {
        ModuleFE_BrowserHistoryV2.set(key, value);
    }
    getQueryParam(key, defaultValue) {
        return ModuleFE_BrowserHistoryV2.get(key) ?? Object.freeze(defaultValue);
    }
    /**
     * Clears the value of a query param by a given key
     * @param key
     */
    deleteQueryParam(key) {
        ModuleFE_BrowserHistoryV2.delete(key);
    }
    // ######################## Multiples
    /**
     * Will merge the given query object into the URL.
     * @param query
     */
    setQueryParams(query) {
        ModuleFE_BrowserHistoryV2.setObject(query);
    }
}
//# sourceMappingURL=ProtoComponent.js.map